import React, { useMemo } from 'react';
import { PetType } from '@spike/model';
import { Room } from '@spike/room-model';
import { OrganizationMarketplaceServicesInfo } from '@spike/marketplace-model';
import Button from 'components/UI/V3/Button/Button';
import { ServiceNameSection } from './Sections/ServiceName/ServiceNameSection';
import { DescriptionSection } from './Sections/Description/DescriptionSection';
import { TimesSection } from './Sections/Times/TimesSection';
import { ColorCodeSection } from './Sections/ColorCode/ColorCodeSection';
import { RoomsAndSpacesSection } from './Sections/RoomsAndSpaces/RoomsAndSpacesSection';
import styles from './BoardingDetailsStep.module.scss';
import { OnlineBookingSection } from './Sections';
import { ServiceFormsSection } from 'components/Service/Forms/ServiceFormsSection';
import { AssociatedForm } from 'model/Forms';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { Service } from 'model/Service';

type TimeMode = '24hours' | 'setTimes';

interface TimeRange {
    from: string;
    to: string;
}

interface BoardingFormData {
    name: string;
    description?: string;
    checkInTime: TimeRange;
    checkOutTime: TimeRange;
    timeMode: TimeMode;
    color?: string;
    onlineBooking: boolean;
    forms?: AssociatedForm[];
    selectedRoom?: string;
    selectedRooms?: Array<string>;
    minNights?: number;
}

interface BoardingDetailsStepProps {
    formData: BoardingFormData;
    petTypes: Array<PetType>;
    marketplaces: Service['marketplaces'];
    availableRooms: Room[];
    roomsLoading: boolean;
    serviceId?: number;
    onNext: (details: {
        name: string;
        description: string;
        checkInTime: TimeRange;
        checkOutTime: TimeRange;
        timeMode: TimeMode;
        color?: string;
        onlineBooking: boolean;
        forms?: AssociatedForm[];
        selectedRooms: Array<string>;
        marketplaces: Service['marketplaces'];
        minNights?: number;
    }) => void;
}

const normalizePetTypeId = (petTypeId: string | number): string => String(petTypeId).toLowerCase().trim();

const roomSupportsAllPetTypes = (
    roomPetTypeIds: Array<string | number>,
    selectedPetTypeIds: Array<string>
): boolean => {
    if (!selectedPetTypeIds.length || !roomPetTypeIds?.length) {
        return false;
    }

    const normalizedRoomIds = roomPetTypeIds.map(normalizePetTypeId);
    return selectedPetTypeIds.map(normalizePetTypeId).every(petTypeId => normalizedRoomIds.includes(petTypeId));
};

const withRoomIds = (
    marketplaces: Service['marketplaces'],
    selectedRooms: Array<string>,
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>
): Service['marketplaces'] => {
    const hasAnyRoomIds = marketplaces.some(marketplace => (marketplace.roomIds ?? []).length > 0);

    return marketplaces.map(marketplace => {
        const existingRoomIds = marketplace.roomIds ?? [];

        if (hasAnyRoomIds || selectedRooms.length === 0) {
            return {
                ...marketplace,
                roomIds: existingRoomIds.slice(0, 1)
            };
        }

        const boardingRooms =
            organizationServicesInfo.find(info => info.marketplace.id === marketplace.marketplaceId)?.rooms?.boarding ??
            [];
        const boardingRoomIds = new Set(boardingRooms.map(room => room.id));
        const migratedRoomId = selectedRooms
            .map(roomId => Number(roomId))
            .find(roomId => !Number.isNaN(roomId) && boardingRoomIds.has(roomId));

        return {
            ...marketplace,
            roomIds: migratedRoomId != null ? [migratedRoomId] : []
        };
    });
};

const sameRoomIds = (left: Array<number> = [], right: Array<number> = []) =>
    left.length === right.length && left.every((roomId, index) => roomId === right[index]);

const sameMarketplaces = (left: Service['marketplaces'], right: Service['marketplaces']) =>
    left.length === right.length &&
    left.every(
        (marketplace, index) =>
            marketplace.marketplaceId === right[index].marketplaceId &&
            sameRoomIds(marketplace.roomIds, right[index].roomIds)
    );

export const BoardingDetailsStep: React.FC<BoardingDetailsStepProps> = props => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const selectedPetTypeIds = useMemo(() => props.petTypes.map(petType => petType.id), [props.petTypes]);
    const selectedRoomsFromForm = useMemo(() => {
        if (props.formData.selectedRooms?.length) {
            return props.formData.selectedRooms;
        }
        return props.formData.selectedRoom ? [props.formData.selectedRoom] : [];
    }, [props.formData.selectedRoom, props.formData.selectedRooms]);
    const marketplaceIdsKey = props.marketplaces.map(marketplace => marketplace.marketplaceId).join(',');
    const selectedRoomsKey = selectedRoomsFromForm.join(',');
    const selectedPetTypeIdsKey = selectedPetTypeIds.join(',');

    const [serviceName, setServiceName] = React.useState({
        value: props.formData.name || '',
        error: ''
    });
    const [description, setDescription] = React.useState({
        value: props.formData.description || '',
        error: ''
    });

    const [timeMode, setTimeMode] = React.useState<TimeMode>(props.formData.timeMode || '24hours');
    const [checkInTime, setCheckInTime] = React.useState(() => ({
        from: props.formData.checkInTime?.from || '2:00 PM',
        to: props.formData.checkInTime?.to || '5:00 PM'
    }));
    const [checkOutTime, setCheckOutTime] = React.useState(() => ({
        from: props.formData.checkOutTime?.from || '9:00 AM',
        to: props.formData.checkOutTime?.to || '12:00 PM'
    }));

    const [colorCode, setColorCode] = React.useState<string | null>(props.formData.color || null);
    const [onlineBooking, setOnlineBooking] = React.useState(props.formData.onlineBooking || false);
    const [forms, setForms] = React.useState<AssociatedForm[]>(props.formData.forms || []);
    const [marketplaces, setMarketplaces] = React.useState<Service['marketplaces']>(() =>
        withRoomIds(props.marketplaces, selectedRoomsFromForm, organizationServicesInfo)
    );

    React.useEffect(() => {
        setServiceName({
            value: props.formData.name || '',
            error: ''
        });
        setDescription({
            value: props.formData.description || '',
            error: ''
        });
        setTimeMode(props.formData.timeMode || '24hours');
        setCheckInTime({
            from: props.formData.checkInTime?.from || '2:00 PM',
            to: props.formData.checkInTime?.to || '5:00 PM'
        });
        setCheckOutTime({
            from: props.formData.checkOutTime?.from || '9:00 AM',
            to: props.formData.checkOutTime?.to || '12:00 PM'
        });
        setColorCode(props.formData.color || null);
        setOnlineBooking(props.formData.onlineBooking || false);
    }, [props.formData]);

    React.useEffect(() => {
        setMarketplaces(prev => {
            const synced = withRoomIds(props.marketplaces, selectedRoomsFromForm, organizationServicesInfo).map(
                marketplace => {
                    const existing = prev.find(item => item.marketplaceId === marketplace.marketplaceId);
                    return {
                        ...marketplace,
                        roomIds: existing?.roomIds ?? marketplace.roomIds ?? []
                    };
                }
            );

            return sameMarketplaces(prev, synced) ? prev : synced;
        });
        // organizationServicesInfo is intentionally omitted: the hook returns a new array every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketplaceIdsKey, selectedRoomsKey]);

    React.useEffect(() => {
        setMarketplaces(prev => {
            let changed = false;
            const next = prev.map(marketplace => {
                const boardingRooms =
                    organizationServicesInfo.find(info => info.marketplace.id === marketplace.marketplaceId)?.rooms
                        ?.boarding ?? [];
                const selectedRoomId = marketplace.roomIds?.[0];
                if (selectedRoomId == null) {
                    return marketplace;
                }

                const room = boardingRooms.find(boardingRoom => boardingRoom.id === selectedRoomId);
                const stillCompatible =
                    room != null && roomSupportsAllPetTypes(room.petTypeIds ?? [], selectedPetTypeIds);

                if (stillCompatible) {
                    return marketplace;
                }

                changed = true;
                return { ...marketplace, roomIds: [] };
            });

            return changed ? next : prev;
        });
        // organizationServicesInfo is intentionally omitted: the hook returns a new array every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPetTypeIdsKey]);

    React.useEffect(() => {
        if (!onlineBooking) {
            return;
        }

        setMarketplaces(prev => {
            let changed = false;
            const next = prev.map(marketplace => {
                const selectedRoomId = marketplace.roomIds?.[0];
                if (selectedRoomId == null) {
                    return marketplace;
                }

                const catalogRoom = props.availableRooms.find(room => room.id === selectedRoomId);
                const isInvalidForOlb =
                    catalogRoom != null && Array.isArray(catalogRoom.rules) && catalogRoom.rules.length === 0;

                if (!isInvalidForOlb) {
                    return marketplace;
                }

                changed = true;
                return { ...marketplace, roomIds: [] };
            });

            return changed ? next : prev;
        });
    }, [onlineBooking, props.availableRooms]);

    const handleServiceNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setServiceName({
            value: e.target.value,
            error: e.target.value.length === 0 ? 'Service name is required' : ''
        });
    }, []);

    const handleDescriptionChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription({
            value: e.target.value,
            error: e.target.value.length > 400 ? 'Description must be less than 400 characters' : ''
        });
    }, []);

    const selectedRooms = useMemo(
        () =>
            Array.from(
                new Set(marketplaces.flatMap(marketplace => (marketplace.roomIds ?? []).map(roomId => String(roomId))))
            ),
        [marketplaces]
    );

    const hasAllRoomsAssigned = React.useMemo(
        () => marketplaces.length > 0 && marketplaces.every(marketplace => (marketplace.roomIds ?? []).length === 1),
        [marketplaces]
    );

    const isValid = React.useMemo(() => {
        const isNameValid = serviceName.error.length === 0 && serviceName.value.length > 0;
        const isCheckInValid = timeMode === '24hours' || (checkInTime.from && checkInTime.to);
        const isCheckOutValid = timeMode === '24hours' || (checkOutTime.from && checkOutTime.to);

        return isNameValid && isCheckInValid && isCheckOutValid && hasAllRoomsAssigned;
    }, [serviceName, timeMode, checkInTime, checkOutTime, hasAllRoomsAssigned]);

    const onOnlineBookingChange = React.useCallback((checked: boolean) => {
        setOnlineBooking(checked);
    }, []);

    const handleNext = React.useCallback(() => {
        if (isValid) {
            props.onNext({
                name: serviceName.value,
                description: description.value,
                checkInTime,
                checkOutTime,
                timeMode,
                color: colorCode || undefined,
                onlineBooking,
                forms,
                selectedRooms,
                marketplaces
            });
        }
    }, [
        serviceName.value,
        description.value,
        checkInTime,
        checkOutTime,
        timeMode,
        colorCode,
        onlineBooking,
        forms,
        selectedRooms,
        marketplaces,
        isValid,
        props
    ]);

    return (
        <div className={styles.container}>
            <ServiceNameSection
                value={serviceName.value}
                error={serviceName.error}
                onChange={handleServiceNameChange}
                className={styles.field}
            />

            <DescriptionSection
                value={description.value}
                error={description.error}
                onChange={handleDescriptionChange}
                className={styles.field}
            />

            <TimesSection
                checkInTime={checkInTime}
                checkOutTime={checkOutTime}
                onCheckInTimeChange={(type, time) => setCheckInTime(prev => ({ ...prev, [type]: time }))}
                onCheckOutTimeChange={(type, time) => setCheckOutTime(prev => ({ ...prev, [type]: time }))}
                onTimeModeChange={setTimeMode}
                timeMode={timeMode}
                checkInLabel="Check-in Time"
                checkOutLabel="Check-out Time"
                className={styles.field}
            />

            <ColorCodeSection
                color={colorCode}
                onChange={setColorCode}
                className={styles.field}
            />

            <OnlineBookingSection
                checked={onlineBooking}
                onChange={onOnlineBookingChange}
                className={styles.field}
            />
            <ServiceFormsSection
                forms={forms}
                serviceId={props.serviceId}
                serviceCategory="boarding"
                onChange={setForms}
            />

            <RoomsAndSpacesSection
                olbEnabled={onlineBooking}
                marketplaces={marketplaces}
                selectedPetTypeIds={selectedPetTypeIds}
                catalogRooms={props.availableRooms}
                onChangeMarketplaces={setMarketplaces}
                className={styles.field}
            />

            <div className={styles.buttonContainer}>
                <Button
                    label="Next"
                    variant="primary"
                    size="large"
                    disabled={!isValid}
                    onClick={handleNext}
                />
                {!hasAllRoomsAssigned && (
                    <span className={styles.roomsRequiredMessage}>
                        All selected location must have a room assigned.
                    </span>
                )}
            </div>
        </div>
    );
};
